/**
 * Remove any facets that are not in the allowed facets specified
 * @param {import('extensions/hawksearch/services/types').SearchResponse} response
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
export const removeFacets = allowedFacets => async (response, next) => {
	response.facets = response?.facets?.filter((facet) => {
		// eslint-disable-next-line no-mixed-operators
		return facet.visible && (allowedFacets && allowedFacets[facet.field] || !allowedFacets);
	});

	await next();
};

/**
 * Remove Lab Grown from the selected facets (to prevent display)
 * @param {import('extensions/hawksearch/services/types').SearchResponse} response
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
export const removeLabGrownFacet = async (response, next) => {
	response.selectedFacets = response.selectedFacets.filter((filter) => {
		return filter.field !== 'labgrown';
	});

	await next();
};

/**
 * Process Setting Images
 * @param {import('extensions/hawksearch/services/search-service').SearchStore} store
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
// eslint-disable-next-line max-lines-per-function
export const processSettingImages = async (store, next) => {
	if (!store?.state?.results?.length) {
		await next();
		return;
	}

	for (const product of store?.state?.results ?? []) {
		product.custom = {
			...product.custom,
			currentSwatch: {},
			images: [],
			settingImage: null,
			settingImageThumbnail: null,
			settingUnderlayImage: null,
			settingUnderlayImageThumbnail: null,
			swatches: []
		};

		if (product?.attributes?.ring_setting_images) {
			product.custom.images = product.attributes.ring_setting_images.filter((value, index, self) => index === self.findIndex((t) => t.image === value.image));

			const mainImage = product.custom.images.find(image => image.type === 'main');
			if (mainImage) {
				product.custom.settingImage = mainImage.image;
				product.custom.settingImageThumbnail = mainImage.thumbnail;
				product.custom.settingUnderlayImage = mainImage.image;
				product.custom.settingUnderlayImageThumbnail = mainImage.thumbnail;
			}

			const rb2Image = product.custom.images.find(image => image.type === 'rb2');
			if (rb2Image) {
				product.custom.settingUnderlayImage = rb2Image.image;
				product.custom.settingUnderlayImageThumbnail = rb2Image.thumbnail;
				if (!product.custom.settingImage) {
					product.custom.settingImage = product.custom.settingUnderlayImage;
					product.custom.settingImageThumbnail = product.custom.settingUnderlayImageThumbnail;
				}

				/*
				 * Set Ring Builder Setting Image to 2nd in the list. Main will be first.
				 * If no Main image, set to the first,
				 */
				product.custom.images = product.custom.images.filter(image => image.type !== 'rb2');
				product.custom.images.splice(Math.min(mainImage ? 1 : 0, product.custom.images.length), 0, rb2Image);
			}
		}

		if (product?.attributes?.options_variant_swatch) {
			for (const variantSwatch of product.attributes.options_variant_swatch) {
				const swatch = variantSwatch;
				try {
					// eslint-disable-next-line camelcase
					swatch.images = JSON.parse(swatch.ring_swatches);
				} catch {
					continue;
				}

				const swatchMainImage = swatch.images.find(image => image.type === 'main');
				swatch.settingImage = swatchMainImage?.image;
				swatch.settingImageThumbnail = swatchMainImage?.thumbnail;
				swatch.settingUnderlayImage = swatchMainImage?.image;
				swatch.settingUnderlayImageThumbnail = swatchMainImage?.thumbnail;

				const swatchrb2Image = swatch.images.find(image => image.type === 'rb2');
				if (swatchrb2Image) {
					swatch.settingUnderlayImage = swatchrb2Image.image;
					swatch.settingUnderlayImageThumbnail = swatchrb2Image.thumbnail;

					// eslint-disable-next-line max-depth
					if (!swatch.settingImage) {
						swatch.settingImage = swatch.settingUnderlayImage;
						swatch.settingImageThumbnail = swatch.settingUnderlayImageThumbnail;
					}
				}

				if (swatch.code === product.id) {
					product.custom.currentSwatch = swatch;
					product.custom.swatches.unshift(swatch);
				} else {
					product.custom.swatches.push(swatch);
				}
			}
		}

	}

	await next();
};

/**
 * Process Facet Transformers
 * @param {import('extensions/hawksearch/services/search-service').SearchStore} store
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
export const processFacetTransformers = transformers => async (store, next) => {
	for (let i = 0, l = store?.state?.facets?.length; i < l; i++) {
		const facet = store.state.facets[i];
		const transformer = transformers[facet.field];
		if (transformer) {
			store.state.facets[i] = transformer(facet);
		}
	}

	await next();
};

/**
 * Process Result Field Transformers
 * @param {import('extensions/hawksearch/services/search-service').SearchStore} store
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
export const processResultFieldTransformers = transformers => async (store, next) => {
	for (let i = 0, l = store?.state?.results?.length; i < l; i++) {
		const result = store.state.results[i];
		for (let field in transformers) {
			result.attributes[field] = transformers[field](result.attributes[field]);
		}
	}

	await next();
};

/**
 * Pluck Lab Grown Facet
 * @param {import('extensions/hawksearch/services/search-service').SearchStore} store
 * @param {import('extensions/hawksearch/services/event-manager/lib/types').Next} next
 */
export const pluckLabGrownFacet = async (store, next) => {
	const labgrownFacetIndex = store?.state?.facets?.findIndex((facet) => {
		return facet.field === 'labgrown';
	});

	if (labgrownFacetIndex > -1) {
		const [labGrownFacet] = store?.state?.facets?.splice(labgrownFacetIndex, 1);
		store.state.custom = {
			...store.state.custom,
			labGrownFacet: labGrownFacet
		};
	}

	await next();
};

/**
 * Save Previous URL
 */
export const savePreviousUrl = (store) => async (eventData, next) => {
	if (store?.step === store?._parentStore?.step) {
		store.setPreviousUrl(window.location.href);
	}
	await next();
};
